/**
 * The shop that did the work, and a job's linked billing papers — TRBUD-412.
 *
 * One company can bill for work another shop did: Best One installs the tires
 * and Goodyear's national account bills for them. Eden's rulings, 22 October
 * 2026:
 *
 * - The biller keeps the spend; the shop's figure is shown beside it, never
 *   added.
 * - Once a shop is recorded, vendor-versus-in-house follows that shop's role.
 * - The AI's DEL BY read pre-fills "Work done by" as a suggestion a person keeps
 *   or clears.
 *
 * A job's papers can also be linked: a second bill for another part of the job
 * (`split_bill`), or the shop's own ticket kept as a record (`provider_record`).
 * Linking never moves money.
 *
 * Browser-safe: zod, types and labels. The writers are the database routines
 * `set_vendor_invoice_performer`, `link_billing` and `end_billing_link`
 * (migration 20261023184113).
 */
import { z } from "zod";

/**
 * Why the shop is known. `printed_on_bill`: the AI read it off the bill (DEL
 * BY) and a person kept it. `provider_document`: the shop's own ticket is on
 * file. `person`: somebody chose it.
 */
export const performedByBases = ["printed_on_bill", "provider_document", "person"] as const;
export type PerformedByBasis = (typeof performedByBases)[number];

export const performedByBasisLabels: Record<PerformedByBasis, string> = {
  printed_on_bill: "Printed on the bill",
  provider_document: "The shop's own record",
  person: "Chosen by a person",
};

export const billingLinkKinds = ["split_bill", "provider_record"] as const;
export type BillingLinkKind = (typeof billingLinkKinds)[number];

export const billingLinkKindLabels: Record<BillingLinkKind, string> = {
  split_bill: "Another bill for other work",
  provider_record: "The shop's own record",
};

/* The same bounds as `idempotencyKey` in `requests.ts`. Not imported: that file
   imports `performedByInput` from here, and a cycle between the two modules
   would read a schema before it was defined. */
const linkIdempotencyKey = z.string().trim().min(8).max(200);

/**
 * The shop on a log body (`POST /work-orders/:id/manual-invoice`,
 * `POST /purchase-orders/:id/bill`). Absent is no other shop: nothing is ever
 * inferred from the biller. Cleared on the form, nothing is sent.
 */
export const performedByInput = z
  .object({
    vendorId: z.string().uuid(),
    basis: z.enum(performedByBases),
  })
  .strict();
export type PerformedByInput = z.infer<typeof performedByInput>;

/**
 * `PUT /vendor-invoices/:id/performed-by`: record, change or clear the shop.
 * A null vendor clears it, and then no basis is sent.
 */
export const setPerformedByBody = z
  .object({
    performedByVendorId: z.string().uuid().nullable(),
    basis: z.enum(performedByBases).optional(),
    idempotencyKey: linkIdempotencyKey,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.performedByVendorId === null && value.basis !== undefined) {
      ctx.addIssue({ code: "custom", path: ["basis"], message: "Clearing the shop takes no basis." });
    }
    if (value.performedByVendorId !== null && value.basis === undefined) {
      ctx.addIssue({ code: "custom", path: ["basis"], message: "Say how the shop is known." });
    }
  });
export type SetPerformedByBody = z.infer<typeof setPerformedByBody>;

/** `POST /work-orders/:id/billing-links`. */
export const createBillingLinkBody = z
  .object({
    kind: z.enum(billingLinkKinds),
    billInvoiceId: z.string().uuid(),
    relatedInvoiceId: z.string().uuid().optional(),
    relatedDocumentId: z.string().uuid().optional(),
    /** The person's statement of the separate work. Required for a second bill. */
    note: z.string().trim().min(1).max(1000).optional(),
    /** Two bills with the same total: the person has checked they are separate charges. */
    sameTotalAcknowledged: z.boolean().optional(),
    idempotencyKey: linkIdempotencyKey,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.kind === "split_bill" && (value.relatedInvoiceId === undefined || value.relatedDocumentId !== undefined)) {
      ctx.addIssue({ code: "custom", path: ["relatedInvoiceId"], message: "A second bill link names the other bill, and no file." });
    }
    if (value.kind === "provider_record" && (value.relatedDocumentId === undefined || value.relatedInvoiceId !== undefined)) {
      ctx.addIssue({ code: "custom", path: ["relatedDocumentId"], message: "A shop's record link names the shop's file, and no bill." });
    }
    if (value.kind === "split_bill" && value.note === undefined) {
      ctx.addIssue({ code: "custom", path: ["note"], message: "Say what separate work each bill is for." });
    }
  });
export type CreateBillingLinkBody = z.infer<typeof createBillingLinkBody>;

/** `POST /billing-links/:id/end`. */
export const endBillingLinkBody = z
  .object({
    reason: z.string().trim().min(1).max(1000),
    idempotencyKey: linkIdempotencyKey,
  })
  .strict();
export type EndBillingLinkBody = z.infer<typeof endBillingLinkBody>;

/** The shop recorded on a bill. */
export interface BillShop {
  vendorId: string;
  name: string;
  basis: PerformedByBasis;
}

/** A file, opened in the page (never downloaded). */
export interface LinkedBillFile {
  documentId: string;
  fileName: string;
  /** The content route shows it in the page (`documentDelivery` is `inline`). */
  canPreview: boolean;
}

/** One bill on the job, as the Linked bills panel shows it. */
export interface LinkedBill {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  /** `vendor_invoice.current_status`. A void bill is listed, and cannot be linked. */
  status: string;
  billerVendorId: string;
  billerName: string;
  shop: BillShop | null;
  currency: string;
  /** The bill's total as printed. Null when this viewer may not see job costs. */
  totalAmount: string | null;
  /** The bill's live posted lines on this job: the biller's spend. Null when withheld. */
  billerAmount: string | null;
  /**
   * The same money as the shop's figure, shown BESIDE the biller's and never
   * added to it. Null with no shop, or when withheld.
   */
  shopAmount: string | null;
  file: LinkedBillFile;
  /** The bill's Review item, to open it there. Null when it has none. */
  reviewItemId: string | null;
}

/** A live link between a bill and another of the job's papers. */
export interface BillingLinkRow {
  linkId: string;
  kind: BillingLinkKind;
  billInvoiceId: string;
  relatedInvoiceId: string | null;
  relatedFile: LinkedBillFile | null;
  note: string | null;
  linkedAt: string;
  linkedByLabel: string;
}

/** `GET /work-orders/:id/linked-bills`. */
export interface LinkedBills {
  workOrderId: string;
  bills: LinkedBill[];
  links: BillingLinkRow[];
  /**
   * Files on the job that may be linked as a shop's record: attached, not a
   * bill, and backing no live cost.
   */
  recordCandidates: LinkedBillFile[];
  /** Whether the figures are shown: job costs at scope all. */
  amountsVisible: boolean;
}

/** What `PUT /vendor-invoices/:id/performed-by` answers. */
export interface PerformedByReceipt {
  invoiceId: string;
  shop: BillShop | null;
}

/** What the two link routes answer. */
export interface BillingLinkReceipt {
  linkId: string;
}
